import { supabase } from './supabase';
import type { BudgetCategory, BudgetExpense, BudgetSummary } from '../types/budget';

export const getBudgetData = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Get user budget
  const { data: budgetData } = await supabase
    .from('user_budgets')
    .select('total_budget')
    .eq('user_id', user.id)
    .single();

  const totalBudget = budgetData?.total_budget || 0;

  // Get categories sorted by allocated amount (largest first)
  const { data: categories, error: categoriesError } = await supabase
    .from('budget_categories')
    .select('*')
    .eq('user_id', user.id)
    .order('allocated', { ascending: false });

  if (categoriesError) throw categoriesError;

  // Get expenses
  const { data: expenses, error: expensesError } = await supabase
    .from('budget_expenses')
    .select(`
      *,
      budget_categories!inner(name)
    `)
    .eq('user_id', user.id)
    .order('date', { ascending: false });

  if (expensesError) throw expensesError;

  // Calculate summary
  const totalAllocated = categories?.reduce((sum, cat) => sum + Number(cat.allocated), 0) || 0;
  const totalSpent = categories?.reduce((sum, cat) => sum + Number(cat.spent), 0) || 0;
  const remaining = totalBudget - totalSpent;
  const percentageSpent = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

  const summary: BudgetSummary = {
    totalBudget,
    totalAllocated,
    totalSpent,
    remaining,
    percentageSpent
  };

  return {
    totalBudget,
    categories: categories || [],
    expenses: expenses || [],
    summary
  };
};

export const updateTotalBudget = async (amount: number) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase
    .from('user_budgets')
    .upsert({
      user_id: user.id,
      total_budget: amount,
      updated_at: new Date().toISOString()
    }, { onConflict: 'user_id' });

  if (error) throw error;
};

export const createBudgetCategory = async (category: Omit<BudgetCategory, 'id' | 'user_id' | 'spent' | 'created_at' | 'updated_at'>) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase
    .from('budget_categories')
    .insert({
      user_id: user.id,
      name: category.name,
      allocated: category.allocated,
      color: category.color,
      icon_name: category.icon_name,
      description: category.description
    });

  if (error) throw error;
};

export const updateBudgetCategory = async (categoryId: string, updates: Partial<BudgetCategory>) => {
  const { error } = await supabase
    .from('budget_categories')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', categoryId);

  if (error) throw error;
};

export const deleteBudgetCategory = async (categoryId: string) => {
  const { error } = await supabase
    .from('budget_categories')
    .delete()
    .eq('id', categoryId);

  if (error) throw error;
};

export const createBudgetExpense = async (expense: {
  category_id: string;
  amount: number;
  description: string;
  date: string;
  vendor?: string;
  notes?: string;
}) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // First, add the expense
  const { error: expenseError } = await supabase
    .from('budget_expenses')
    .insert({
      user_id: user.id,
      category_id: expense.category_id,
      amount: expense.amount,
      description: expense.description,
      date: expense.date,
      vendor: expense.vendor,
      notes: expense.notes
    });

  if (expenseError) throw expenseError;

  // Then update the category's spent amount
  const { data: categoryExpenses } = await supabase
    .from('budget_expenses')
    .select('amount')
    .eq('category_id', expense.category_id);

  const totalSpent = categoryExpenses?.reduce((sum, exp) => sum + Number(exp.amount), 0) || 0;

  const { error: updateError } = await supabase
    .from('budget_categories')
    .update({ spent: totalSpent })
    .eq('id', expense.category_id);

  if (updateError) throw updateError;
};

export const updateBudgetExpense = async (expenseId: string, updates: {
  category_id: string;
  amount: number;
  description: string;
  date: string;
  vendor?: string;
  notes?: string;
}) => {
  // Get the current expense to know the old category
  const { data: currentExpense } = await supabase
    .from('budget_expenses')
    .select('category_id, amount')
    .eq('id', expenseId)
    .single();

  if (!currentExpense) throw new Error('Expense not found');

  // Update the expense
  const { error: updateError } = await supabase
    .from('budget_expenses')
    .update({
      category_id: updates.category_id,
      amount: updates.amount,
      description: updates.description,
      date: updates.date,
      vendor: updates.vendor,
      notes: updates.notes
    })
    .eq('id', expenseId);

  if (updateError) throw updateError;

  // Update spent amounts for both old and new categories
  const categoriesToUpdate = [currentExpense.category_id];
  if (updates.category_id !== currentExpense.category_id) {
    categoriesToUpdate.push(updates.category_id);
  }

  for (const categoryId of categoriesToUpdate) {
    const { data: categoryExpenses } = await supabase
      .from('budget_expenses')
      .select('amount')
      .eq('category_id', categoryId);

    const totalSpent = categoryExpenses?.reduce((sum, exp) => sum + Number(exp.amount), 0) || 0;

    await supabase
      .from('budget_categories')
      .update({ spent: totalSpent })
      .eq('id', categoryId);
  }
};

export const deleteBudgetExpense = async (expenseId: string) => {
  // Get the expense to know which category to update
  const { data: expense } = await supabase
    .from('budget_expenses')
    .select('category_id')
    .eq('id', expenseId)
    .single();

  if (!expense) throw new Error('Expense not found');

  // Delete the expense
  const { error: deleteError } = await supabase
    .from('budget_expenses')
    .delete()
    .eq('id', expenseId);

  if (deleteError) throw deleteError;

  // Update the category's spent amount
  const { data: categoryExpenses } = await supabase
    .from('budget_expenses')
    .select('amount')
    .eq('category_id', expense.category_id);

  const totalSpent = categoryExpenses?.reduce((sum, exp) => sum + Number(exp.amount), 0) || 0;

  const { error: updateError } = await supabase
    .from('budget_categories')
    .update({ spent: totalSpent })
    .eq('id', expense.category_id);

  if (updateError) throw updateError;
};

export const initializeDefaultCategories = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Check if user already has categories
  const { data: existingCategories } = await supabase
    .from('budget_categories')
    .select('id')
    .eq('user_id', user.id)
    .limit(1);

  if (existingCategories && existingCategories.length > 0) {
    return; // User already has categories
  }

  // Default categories for wedding planning
  const defaultCategories = [
    {
      name: 'Venue',
      allocated: 0,
      color: '#3B82F6',
      icon_name: 'Target',
      description: 'Reception and ceremony venue costs'
    },
    {
      name: 'Catering',
      allocated: 0,
      color: '#10B981',
      icon_name: 'Receipt',
      description: 'Food and beverage expenses'
    },
    {
      name: 'Photography',
      allocated: 0,
      color: '#F59E0B',
      icon_name: 'Calendar',
      description: 'Photography and videography services'
    },
    {
      name: 'Decorations',
      allocated: 0,
      color: '#EF4444',
      icon_name: 'DollarSign',
      description: 'Flowers, centerpieces, and decorations'
    },
    {
      name: 'Attire',
      allocated: 0,
      color: '#8B5CF6',
      icon_name: 'TrendingUp',
      description: 'Wedding dress, suit, and accessories'
    },
    {
      name: 'Music & Entertainment',
      allocated: 0,
      color: '#06B6D4',
      icon_name: 'PieChart',
      description: 'DJ, band, or entertainment services'
    }
  ];

  // Insert default categories
  const { error } = await supabase
    .from('budget_categories')
    .insert(
      defaultCategories.map(category => ({
        user_id: user.id,
        ...category
      }))
    );

  if (error) throw error;
};